import type {
  AcademicLevelResponse,
  CourseSectionDetailResponse,
  CourseSectionStatus,
  CourseSectionSummaryResponse,
  CreateAcademicLevelRequest,
  CreateCourseSectionRequest,
  CreateHomologationRequest,
  CreateSchoolRequest,
  CreateSubjectRequest,
  CreateTeacherRequest,
  EndEnrollmentRequest,
  EnrollPersonRequest,
  HomologationResponse,
  HomologationSourceType,
  HomologationStatus,
  PaginatedResponse,
  RecordGradeRequest,
  ReviewHomologationRequest,
  SchoolResponse,
  SubjectResponse,
  TeacherResponse,
  TranscriptEntryResponse,
  UpdateAcademicLevelRequest,
  UpdateCourseSectionRequest,
  UpdateSchoolRequest,
  UpdateSubjectRequest,
  UpdateTeacherRequest,
} from '@tabita/contracts';
import { apiRequest } from './api-client';

/**
 * The Academic endpoints — schools, levels, subjects, teachers, course
 * sections, enrollments (staff and self-service), transcripts and
 * homologations. Request and response types come from `@tabita/contracts` —
 * the same definitions the server validates against.
 */
export interface ListCourseSectionsParams {
  readonly schoolId?: string | undefined;
  readonly subjectId?: string | undefined;
  readonly status?: CourseSectionStatus | undefined;
  readonly campusId?: string | undefined;
  readonly hasOpenCapacity?: boolean | undefined;
  readonly includeArchived?: boolean | undefined;
  readonly cursor?: string | undefined;
  readonly limit?: number | undefined;
}

export const academicApi = {
  // --- Schools ------------------------------------------------------------------

  listSchools(includeArchived = false, signal?: AbortSignal) {
    return apiRequest<readonly SchoolResponse[]>('/academic/schools', {
      query: { includeArchived },
      signal,
    });
  },
  getSchool(id: string, signal?: AbortSignal) {
    return apiRequest<SchoolResponse>(`/academic/schools/${id}`, { signal });
  },
  createSchool(body: CreateSchoolRequest) {
    return apiRequest<SchoolResponse>('/academic/schools', { method: 'POST', body });
  },
  updateSchool(id: string, body: UpdateSchoolRequest) {
    return apiRequest<SchoolResponse>(`/academic/schools/${id}`, { method: 'PATCH', body });
  },
  archiveSchool(id: string) {
    return apiRequest<SchoolResponse>(`/academic/schools/${id}/archive`, { method: 'POST' });
  },
  restoreSchool(id: string) {
    return apiRequest<SchoolResponse>(`/academic/schools/${id}/restore`, { method: 'POST' });
  },

  // --- Levels (Pensum) ------------------------------------------------------------

  listLevels(schoolId: string, includeArchived = false, signal?: AbortSignal) {
    return apiRequest<readonly AcademicLevelResponse[]>(`/academic/schools/${schoolId}/levels`, {
      query: { includeArchived },
      signal,
    });
  },
  createLevel(schoolId: string, body: CreateAcademicLevelRequest) {
    return apiRequest<AcademicLevelResponse>(`/academic/schools/${schoolId}/levels`, {
      method: 'POST',
      body,
    });
  },
  updateLevel(id: string, body: UpdateAcademicLevelRequest) {
    return apiRequest<AcademicLevelResponse>(`/academic/levels/${id}`, { method: 'PATCH', body });
  },
  archiveLevel(id: string) {
    return apiRequest<AcademicLevelResponse>(`/academic/levels/${id}/archive`, { method: 'POST' });
  },
  restoreLevel(id: string) {
    return apiRequest<AcademicLevelResponse>(`/academic/levels/${id}/restore`, { method: 'POST' });
  },

  // --- Subjects ("Materias") --------------------------------------------------------

  listSubjects(schoolId: string, includeArchived = false, signal?: AbortSignal) {
    return apiRequest<readonly SubjectResponse[]>(`/academic/schools/${schoolId}/subjects`, {
      query: { includeArchived },
      signal,
    });
  },
  getSubject(id: string, signal?: AbortSignal) {
    return apiRequest<SubjectResponse>(`/academic/subjects/${id}`, { signal });
  },
  createSubject(schoolId: string, body: CreateSubjectRequest) {
    return apiRequest<SubjectResponse>(`/academic/schools/${schoolId}/subjects`, {
      method: 'POST',
      body,
    });
  },
  updateSubject(id: string, body: UpdateSubjectRequest) {
    return apiRequest<SubjectResponse>(`/academic/subjects/${id}`, { method: 'PATCH', body });
  },
  archiveSubject(id: string) {
    return apiRequest<SubjectResponse>(`/academic/subjects/${id}/archive`, { method: 'POST' });
  },
  restoreSubject(id: string) {
    return apiRequest<SubjectResponse>(`/academic/subjects/${id}/restore`, { method: 'POST' });
  },

  // --- Teachers -------------------------------------------------------------------

  listTeachers(schoolId: string, includeArchived = false, signal?: AbortSignal) {
    return apiRequest<readonly TeacherResponse[]>(`/academic/schools/${schoolId}/teachers`, {
      query: { includeArchived },
      signal,
    });
  },
  createTeacher(schoolId: string, body: CreateTeacherRequest) {
    return apiRequest<TeacherResponse>(`/academic/schools/${schoolId}/teachers`, {
      method: 'POST',
      body,
    });
  },
  updateTeacher(id: string, body: UpdateTeacherRequest) {
    return apiRequest<TeacherResponse>(`/academic/teachers/${id}`, { method: 'PATCH', body });
  },
  archiveTeacher(id: string) {
    return apiRequest<TeacherResponse>(`/academic/teachers/${id}/archive`, { method: 'POST' });
  },
  restoreTeacher(id: string) {
    return apiRequest<TeacherResponse>(`/academic/teachers/${id}/restore`, { method: 'POST' });
  },

  // --- Course sections ("Cursos") --------------------------------------------------

  listCourseSections(params: ListCourseSectionsParams = {}, signal?: AbortSignal) {
    return apiRequest<PaginatedResponse<CourseSectionSummaryResponse>>(
      '/academic/course-sections',
      {
        query: { ...params },
        signal,
      },
    );
  },
  getCourseSection(id: string, signal?: AbortSignal) {
    return apiRequest<CourseSectionDetailResponse>(`/academic/course-sections/${id}`, { signal });
  },
  createCourseSection(body: CreateCourseSectionRequest) {
    return apiRequest<CourseSectionDetailResponse>('/academic/course-sections', {
      method: 'POST',
      body,
    });
  },
  updateCourseSection(id: string, body: UpdateCourseSectionRequest) {
    return apiRequest<CourseSectionDetailResponse>(`/academic/course-sections/${id}`, {
      method: 'PATCH',
      body,
    });
  },
  archiveCourseSection(id: string, reason: string) {
    return apiRequest<CourseSectionDetailResponse>(`/academic/course-sections/${id}/archive`, {
      method: 'POST',
      body: { reason },
    });
  },
  restoreCourseSection(id: string) {
    return apiRequest<CourseSectionDetailResponse>(`/academic/course-sections/${id}/restore`, {
      method: 'POST',
    });
  },

  // --- Enrollments ------------------------------------------------------------------

  enroll(sectionId: string, body: EnrollPersonRequest) {
    return apiRequest<CourseSectionDetailResponse>(
      `/academic/course-sections/${sectionId}/enrollments`,
      { method: 'POST', body },
    );
  },
  /** "Auto-Matrícula" — the caller matriculates their own linked Person. */
  enrollSelf(sectionId: string) {
    return apiRequest<CourseSectionDetailResponse>(
      `/academic/course-sections/${sectionId}/enrollments/self`,
      { method: 'POST' },
    );
  },
  endEnrollment(sectionId: string, personId: string, body: EndEnrollmentRequest) {
    return apiRequest<CourseSectionDetailResponse>(
      `/academic/course-sections/${sectionId}/enrollments/${personId}`,
      { method: 'DELETE', body },
    );
  },
  recordGrade(sectionId: string, personId: string, body: RecordGradeRequest) {
    return apiRequest<CourseSectionDetailResponse>(
      `/academic/course-sections/${sectionId}/enrollments/${personId}/grade`,
      { method: 'PATCH', body },
    );
  },

  // --- Transcripts ("Historial de Calificaciones") -----------------------------------

  transcriptFor(personId: string, signal?: AbortSignal) {
    return apiRequest<readonly TranscriptEntryResponse[]>(
      `/academic/people/${personId}/transcript`,
      { signal },
    );
  },
  myTranscript(signal?: AbortSignal) {
    return apiRequest<readonly TranscriptEntryResponse[]>('/academic/my/transcript', { signal });
  },

  // --- Homologations ------------------------------------------------------------------

  listHomologations(
    params: { schoolId?: string; personId?: string; status?: HomologationStatus } = {},
    signal?: AbortSignal,
  ) {
    return apiRequest<readonly HomologationResponse[]>('/academic/homologations', {
      query: { ...params },
      signal,
    });
  },
  myHomologations(signal?: AbortSignal) {
    return apiRequest<readonly HomologationResponse[]>('/academic/my/homologations', { signal });
  },
  requestHomologation(schoolId: string, body: CreateHomologationRequest) {
    return apiRequest<HomologationResponse>(`/academic/schools/${schoolId}/homologations`, {
      method: 'POST',
      body,
    });
  },
  reviewHomologation(id: string, body: ReviewHomologationRequest) {
    return apiRequest<HomologationResponse>(`/academic/homologations/${id}/review`, {
      method: 'POST',
      body,
    });
  },
};

export type { HomologationSourceType };
